import { hasPermission } from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { AlertTriangle, Inbox } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge, Card, Skeleton } from '@/components/ui/field'
import { Checkbox, Textarea } from '@/components/ui/select'
import { clientsCountQuery, clientsQuery, useBulkClients } from '@/features/clients/api'
import { settingsQuery } from '@/features/review/api'
import { ReviewDrawer } from '@/features/review/review-drawer'
import { ApiClientError } from '@/lib/api'
import { fetchMe } from '@/lib/auth-query'
import { formatDateTime } from '@/lib/utils'

export const Route = createFileRoute('/_app/review')({
  beforeLoad: async ({ context }) => {
    const me = await fetchMe(context.queryClient)
    if (me && !hasPermission(me.user.role, 'clients:review')) {
      throw redirect({ to: '/' })
    }
  },
  component: ReviewPage,
})

/**
 * The pending-review queue (PLAN §6). Oldest first — a self-registration is a
 * person waiting for a call, so the queue is FIFO, unlike the client list's
 * newest-first ordering.
 */
const QUEUE_FILTER = { status: ['pending_review'] }

function ReviewPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [reviewing, setReviewing] = useState<string | null>(null)

  const settings = useQuery(settingsQuery)
  const list = useQuery(clientsQuery({ ...QUEUE_FILTER, limit: 100 }))
  const count = useQuery(clientsCountQuery(QUEUE_FILTER))

  // The API orders newest-first; the queue wants the person who has waited longest.
  const items = [...(list.data?.items ?? [])].reverse()
  const total = count.data?.total ?? 0
  const autoWelcome = settings.data?.settings.autoWelcomeOnConfirm ?? false

  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id))

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Review queue</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {count.isLoading
            ? 'Counting…'
            : total === 0
              ? 'Nothing waiting'
              : `${total.toLocaleString()} registration${total === 1 ? '' : 's'} waiting, oldest first`}
        </p>
      </div>

      {selected.size > 0 && (
        <BulkReviewBar
          selectedIds={[...selected]}
          total={total}
          pageCount={items.length}
          autoWelcome={autoWelcome}
          onClear={() => setSelected(new Set())}
        />
      )}

      {list.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-3/4" />
        </div>
      )}

      {!list.isLoading && items.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Inbox className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm font-medium">The queue is empty</p>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Self-registrations from your{' '}
            <Link to="/links" className="underline">
              registration links
            </Link>{' '}
            land here for confirmation.
          </p>
        </Card>
      )}

      {items.length > 0 && (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-muted)]/50 px-4 py-2">
            <Checkbox
              checked={allSelected}
              onChange={() =>
                setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)))
              }
              aria-label="Select all shown"
            />
            <span className="text-xs text-[var(--color-muted-foreground)]">
              Select all {items.length} shown
            </span>
          </div>

          <ul className="divide-y divide-[var(--color-border)]">
            {items.map((client) => (
              <li
                key={client.id}
                className="flex items-center gap-3 p-3 hover:bg-[var(--color-muted)]/30"
              >
                <Checkbox
                  checked={selected.has(client.id)}
                  onChange={() =>
                    setSelected((prev) => {
                      const next = new Set(prev)
                      if (next.has(client.id)) next.delete(client.id)
                      else next.add(client.id)
                      return next
                    })
                  }
                  aria-label={`Select ${client.fullName}`}
                />

                <button
                  type="button"
                  onClick={() => setReviewing(client.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm font-medium">{client.fullName}</p>
                  <p className="truncate text-xs text-[var(--color-muted-foreground)]">
                    {client.phone}
                    {client.district && ` · ${client.district}`}
                    {' · '}
                    {formatDateTime(client.createdAt)}
                  </p>
                </button>

                <div className="flex shrink-0 items-center gap-2">
                  {client.countries.length > 0 && (
                    <Badge variant="muted">{client.countries.length} countries</Badge>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setReviewing(client.id)}>
                    Review
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {reviewing && (
        <ReviewDrawer
          clientId={reviewing}
          autoWelcome={autoWelcome}
          onClose={() => setReviewing(null)}
          onDecided={() => {
            setReviewing(null)
            setSelected((prev) => {
              const next = new Set(prev)
              next.delete(reviewing)
              return next
            })
          }}
        />
      )}
    </div>
  )
}

function BulkReviewBar({
  selectedIds,
  total,
  pageCount,
  autoWelcome,
  onClear,
}: {
  selectedIds: string[]
  total: number
  pageCount: number
  autoWelcome: boolean
  onClear: () => void
}) {
  const bulk = useBulkClients()
  const [confirming, setConfirming] = useState<'confirm' | 'reject' | null>(null)
  const [allMatching, setAllMatching] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const affected = allMatching ? total : selectedIds.length

  function run() {
    if (!confirming) return
    setError(null)

    bulk.mutate(
      {
        action: confirming,
        ...(confirming === 'reject' ? { reason: reason.trim() } : {}),
        ...(allMatching
          ? { filter: QUEUE_FILTER as never, expectedCount: total }
          : { ids: selectedIds }),
      },
      {
        onSuccess: () => {
          setConfirming(null)
          setAllMatching(false)
          setReason('')
          onClear()
        },
        onError: (e) => setError(e instanceof ApiClientError ? e.message : 'The action failed'),
      },
    )
  }

  return (
    <>
      <Card className="flex flex-wrap items-center gap-3 border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3">
        <span className="text-sm font-medium">{affected.toLocaleString()} selected</span>

        {/* Only offer confirm-all when there's genuinely more than what's shown. */}
        {!allMatching && selectedIds.length >= pageCount && total > selectedIds.length && (
          <Button variant="link" size="sm" onClick={() => setAllMatching(true)}>
            Select all {total.toLocaleString()} waiting
          </Button>
        )}
        {allMatching && (
          <span className="text-xs text-[var(--color-muted-foreground)]">
            Every registration in the queue
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirming('reject')}>
            Reject
          </Button>
          <Button size="sm" onClick={() => setConfirming('confirm')}>
            Confirm {affected.toLocaleString()}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClear}>
            Clear
          </Button>
        </div>

        {error && <p className="w-full text-sm text-[var(--color-destructive)]">{error}</p>}
      </Card>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-md p-6">
            <h2 className="text-lg font-semibold">
              {confirming === 'confirm' ? 'Confirm registrations' : 'Reject registrations'}
            </h2>
            <p className="mt-2 text-sm">
              This will {confirming} <strong>{affected.toLocaleString()}</strong> registration
              {affected === 1 ? '' : 's'}.
            </p>
            {allMatching && (
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                That includes registrations not shown on screen.
              </p>
            )}
            {/* The cost warning: 50 confirmations can mean 50 paid messages. */}
            {confirming === 'confirm' && autoWelcome && (
              <p className="mt-2 rounded-md bg-[var(--color-warning)]/10 px-3 py-2 text-sm text-[var(--color-warning)]">
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                {affected.toLocaleString()} welcome message{affected === 1 ? '' : 's'} will be sent.
              </p>
            )}

            {confirming === 'reject' && (
              <div className="mt-3">
                <Textarea
                  autoFocus
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (recorded on every rejected client)"
                  rows={2}
                />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(null)} disabled={bulk.isPending}>
                Cancel
              </Button>
              <Button
                variant={confirming === 'reject' ? 'destructive' : 'default'}
                loading={bulk.isPending}
                disabled={confirming === 'reject' && !reason.trim()}
                onClick={run}
              >
                Yes, {confirming} {affected.toLocaleString()}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  )
}
